// Mirrored-selection-state + commit machinery, shared by the toolbar and the
// Inspector (each mounts its own instance; both re-mirror on object:modified
// so they never drift, and redundant commits are free — save() dedupes).
//
// apply(fn, mode): mutate through the editor API, re-mirror, record history.
//   'now'   — discrete action, one entry immediately (default)
//   'soon'  — typed/wheeled input (number fields, color wheels): trailing
//             500 ms debounce so a burst lands as one entry
//   'defer' — slider ticks; the slider's own pointer-up calls commitNow
import { useCallback, useEffect, useRef, useState } from 'react';
// Universal effects (U1): kind + dual-read slot bag mirrors for EVERY object
// kind — U2's tile gating reads objProps.effects/objectKind against the
// matrix. capabilityMatrix is pure data/functions (no fabric, no fx
// modules), so the import-free-of-fx-modules convention of this hook holds.
import { effectsBagOf, objectKindOf } from '../lib/fx/capabilityMatrix';

function isTextType(type) {
  // v6: the live .type getter lowercases the class name, so IText reports
  // 'itext' (no hyphen); 'i-text' kept for v5-serialized JSON type fields.
  return type === 'text' || type === 'i-text' || type === 'itext' || type === 'textbox';
}

export function useSelectionProps(editor, selectedObjects) {
  const selectedObject = selectedObjects?.[0];
  const [objProps, setObjProps] = useState({});

  const refresh = useCallback(() => {
    const obj = editor?.canvas?.getActiveObject() || selectedObject;
    if (!obj) {
      setObjProps({});
      return;
    }
    const next = {};
    next.fill = typeof obj.fill === 'string' && obj.fill !== 'transparent' ? obj.fill : '#000000';
    next.hasGradientFill = !!(obj.fill && typeof obj.fill === 'object');
    next.opacity = obj.opacity ?? 1;
    if (obj.type === 'rect') next.cornerRadius = Math.round(obj.rx || 0);
    const stroke = obj.stroke;
    const hasVisibleStroke = !!stroke && stroke !== 'transparent' && (obj.strokeWidth ?? 0) > 0;
    next.borderColor = hasVisibleStroke && typeof stroke === 'string' ? stroke : '#000000';
    next.borderWidth = hasVisibleStroke ? obj.strokeWidth : 0;
    const dash = obj.strokeDashArray;
    next.strokeStyle = !dash || dash.length === 0 ? 'solid' : dash[0] <= 3 ? 'dotted' : 'dashed';

    if (isTextType(obj.type)) {
      next.fontFamily = obj.fontFamily || 'Arial';
      next.fontSize = Math.round(obj.fontSize || 32);
      next.fontWeight = obj.fontWeight || 400;
      next.fontStyle = obj.fontStyle || 'normal';
      next.underline = !!obj.underline;
      next.linethrough = !!obj.linethrough;
      next.textAlign = obj.textAlign || 'left';
      next.charSpacing = obj.charSpacing ?? 0;
      next.lineHeight = obj.lineHeight ?? 1.16;
      next.shadow = obj.shadow
        ? {
            color: obj.shadow.color || '#000000',
            blur: obj.shadow.blur ?? 5,
            offsetX: obj.shadow.offsetX ?? 2,
            offsetY: obj.shadow.offsetY ?? 2,
          }
        : null;
      next.outline = hasVisibleStroke ? { color: stroke, width: obj.strokeWidth } : null;
      next.curveAmount = obj.curveAmount || 0;
      next.curveShape = obj.curveShape || 'arc';
      // Curving is single-line only (fabric's text-on-path has no line concept
      // — every line would land on the same path). Mirror WHY it's blocked so
      // the panel's hint can name the fix: 'break' = the teacher's own line
      // break, 'wrap' = the text box is too narrow for the words.
      next.curveBlocked = String(obj.text ?? '').includes('\n')
        ? 'break'
        : (obj._textLines?.length ?? 1) > 1 ? 'wrap' : null;
      next.textBackgroundColor = obj.textBackgroundColor || null;

      // Style-tab mirrors. Recipes ride obj.data (the source of truth); the
      // live-fill fallbacks keep the panel functional for objects that
      // predate the inspector or lost their data bag (gradient stops are
      // recoverable, the angle is not).
      next.textFill = obj.data?.textFill
        ?? (obj.fill?.colorStops?.length
          ? {
              type: obj.fill.type === 'radial' ? 'radial-gradient' : 'linear-gradient',
              stops: obj.fill.colorStops.map((s) => ({ offset: s.offset, color: s.color })),
            }
          : { type: 'solid', color: typeof obj.fill === 'string' ? obj.fill : '#000000' });
      next.textEffects = obj.data?.textEffects ?? null;
      next.letterCase = obj.data?.letterCase ?? 'none';
      next.listMode = obj.data?.list?.mode ?? 'none';
      // Fabric has no hanging indent: a wrapped item continues under the
      // bullet, not beside it. Mirror the fact (rendered lines outnumber the
      // items) so the panel can say so only when it actually happens.
      next.listWraps = !!obj.data?.list
        && (obj._textLines?.length ?? 0) > String(obj.text ?? '').split('\n').length;
      next.highlightRecipe = obj.data?.highlight
        ?? (obj.textBackgroundColor ? { color: obj.textBackgroundColor, fade: 0 } : null);
      next.blendMode = obj.globalCompositeOperation ?? 'source-over';
      next.isEditing = !!obj.isEditing;
      // Per-character emphasis: the overrides common to the characters the
      // teacher has highlighted (or to the whole string when none are).
      // Sparse — an absent key means "no override", so the controls fall back
      // to the object's own props above.
      next.selectionStyle = editor?.getSelectionTextStyle?.() ?? null;
      // Whether the selection-aware controls would hit a highlighted run or
      // the whole object — the Color section's hint says which, so it must
      // be mirrored.
      next.hasCharSelection = !!obj.isEditing && obj.selectionStart !== obj.selectionEnd;
      // The live range itself (grapheme indices), null when collapsed. The
      // ribbon's scope chip derives its face from the object at render time —
      // this mirror exists so the IDENTITY-STABLE bag (below) still re-renders
      // consumers exactly when the highlight moves, and caret-only keystrokes
      // (start===end throughout) stay render-free.
      next.selRange = next.hasCharSelection
        ? [Math.min(obj.selectionStart, obj.selectionEnd), Math.max(obj.selectionStart, obj.selectionEnd)]
        : null;
      // Per-char-state mirrors (2026-07-19): hasCharStyles shows/hides the
      // Color section's "Reset character styling" link; hasScriptStyles is
      // the Raised & lowered accordion's ACTIVE dot. Cheap object-walk over
      // the runtime styles map — no engine calls.
      let anyCharStyle = false;
      let anyScript = false;
      const lines = obj.styles && typeof obj.styles === 'object' ? Object.values(obj.styles) : [];
      for (const line of lines) {
        if (!line || typeof line !== 'object') continue;
        for (const ch of Object.values(line)) {
          if (ch && typeof ch === 'object' && Object.keys(ch).length > 0) {
            anyCharStyle = true;
            if (ch.deltaY) anyScript = true;
          }
        }
        if (anyCharStyle && anyScript) break;
      }
      next.hasCharStyles = anyCharStyle; // Reset-character-styling visibility
      next.hasScriptStyles = anyScript; // Raised & lowered dot
      // ELA tools dot: the phonics recipe rides obj.data (same sanctioned
      // peek PhonicsControls itself reads).
      next.phonicsMode = obj.data?.phonics?.mode ?? 'none';
    }

    if (obj.type === 'image') {
      next.iconColor = obj.iconColor || '#000000';
      // Frame mask (applyFrameShape writes obj.frameShape) — mirrored so the
      // ribbon's Frame select re-renders through the stable-bag rule.
      next.frameShape = obj.frameShape || 'none';
      next.filters = {
        brightness: editor?.getImageFilterValue('Brightness') ?? 0,
        contrast: editor?.getImageFilterValue('Contrast') ?? 0,
        saturation: editor?.getImageFilterValue('Saturation') ?? 0,
        blur: editor?.getImageFilterValue('Blur') ?? 0,
        grayscale: editor?.hasImageFilter('Grayscale') || false,
        sepia: editor?.hasImageFilter('Sepia') || false,
        invert: editor?.hasImageFilter('Invert') || false,
      };
      const cropEl = obj._originalElement || obj._element;
      const cropNatW = cropEl?.naturalWidth || cropEl?.width || obj.width;
      const cropNatH = cropEl?.naturalHeight || cropEl?.height || obj.height;
      next.hasCrop =
        !!obj.clipPath ||
        (obj.cropX || 0) > 0 ||
        (obj.cropY || 0) > 0 ||
        obj.width < cropNatW ||
        obj.height < cropNatH;
      next.effectiveDPI = editor?.getEffectiveDPI?.(obj) ?? null;
    }

    // Universal effects (U1): the effect-capable kind and the slot bag for
    // ANY object. `effects` dual-reads (data.effects on shapes/icons,
    // data.textEffects on text — same entries the text mirror above carries,
    // one identity), so U2's tiles light for every kind through one mirror.
    next.objectKind = objectKindOf(obj);
    next.effects = effectsBagOf(obj) ?? null;

    // Live fx (any object type): the Bevel/Extrude depth sliders read these.
    // 0 when the step is absent — patchTextFx drops the step at depth 0, so
    // absence and zero are the same state on purpose.
    const fxSteps = Array.isArray(obj.data?.fx) ? obj.data.fx : [];
    const bevelStep = fxSteps.find((s) => s?.id === 'bevel');
    next.fxBevelDepth = Math.round(bevelStep?.params?.depth ?? 0);
    // Bevel width/shape/light (light-shape-finish lane, 2026-07-19). The 34
    // fallback = FX_BEVEL_DEFAULTS.size (kept literal — this hook stays
    // import-free of the fx modules, same convention as the depth mirrors).
    next.fxBevelWidth = Math.round(bevelStep?.params?.size ?? 34);
    next.fxBevelShape = bevelStep?.params?.technique === 'chisel' ? 'chisel' : 'smooth';
    next.fxBevelLightMode = bevelStep?.params?.lightMode === 'poster' ? 'poster' : 'custom';
    // Bevel finish (finish/edge-law lane, 2026-07-20). satin fallback =
    // FX_BEVEL_DEFAULTS.finish (kept literal — import-free, as above).
    next.fxBevelFinish = bevelStep?.params?.finish ?? 'satin';
    const extrudeStep = fxSteps.find((s) => s?.id === 'extrude');
    next.fxExtrudeDepth = Math.round(extrudeStep?.params?.depth ?? 0);
    // Volume style (Depth Everywhere T1): absent = 'solid' (never stored).
    next.fxVolumeStyle = extrudeStep?.params?.volumeStyle ?? 'solid';
    next.fxVolumeRound = Math.round(extrudeStep?.params?.volumeRound ?? 55);
    next.fxVolumeBack = extrudeStep?.params?.volumeBack === 'round' ? 'round' : 'flat';
    // 3D finish chips + light row mirrors (educator labels live in the panel;
    // these carry the internal ids: archetype / lightPreset / lightMode).
    next.fxExtrudeFinish = extrudeStep?.params?.archetype ?? 'satin';
    next.fxExtrudeLightPreset = extrudeStep?.params?.lightPreset ?? 'studio';
    next.fxExtrudeLightMode = extrudeStep?.params?.lightMode === 'poster' ? 'poster' : 'custom';
    // Sides dress (dome-parity lane): null = match the letters (sanitize
    // drops the key), '#hex' = solid dye, else a manifest texture id.
    next.fxExtrudeSideFill = extrudeStep?.params?.sideFill ?? null;
    // The document's shared sun (workspace data) — the poster-light dial
    // reads this; apply() re-mirrors after every setPosterLight tick.
    next.posterLight = editor?.getPosterLight?.() ?? null;
    // 3D gating mirror: the panel disables (with plain-words hints) the
    // controls that cannot mean anything on a baked 3D raster — curve tiles,
    // Hollow/Trace/Sticker, Raised & lowered. Step presence IS activity:
    // patchTextFx drops the step at depth 0, so a lingering zero-depth step
    // cannot exist through the UI.
    next.is3dActive = !!extrudeStep;
    // Sticker-plate mini-mirror: null while extrude is off; the UI's plate
    // row builds its full patch from this (patchTextFx merges shallow — the
    // plate object is always sent whole).
    next.fxExtrudePlate = extrudeStep
      ? {
          enabled: !!extrudeStep.params?.plate?.enabled,
          width: Math.round(extrudeStep.params?.plate?.width ?? 50),
          depth: Math.round(extrudeStep.params?.plate?.depth ?? 40),
          fill: extrudeStep.params?.plate?.fill ?? null,
        }
      : null;

    // Generic shadow (non-text objects too — used by EffectsTab)
    if (!isTextType(obj.type) && obj.shadow) {
      next.shadow = {
        color: obj.shadow.color || '#000000',
        blur: obj.shadow.blur ?? 5,
        offsetX: obj.shadow.offsetX ?? 2,
        offsetY: obj.shadow.offsetY ?? 2,
      };
    }

    // Position tab payload
    next.position = editor?.getPositionInfo?.() ?? null;

    // IDENTITY-STABLE mirrors (R2 ribbon perf, caught by probe-schoolpc's
    // keystroke cell): fabric fires text:selection:changed on EVERY keystroke
    // (the caret is a collapsed selection), and an always-new bag identity
    // re-rendered every mirror consumer — with both ribbon rows mounted that
    // pushed typing p95 past the school-PC budget. The bag is pure data by
    // construction (JSON-serializable mirrors), so value-equality is exact;
    // an unchanged bag keeps its identity and React skips the subtree.
    // Real changes always re-render: commits bump historyIndex (parent tree),
    // and everything the ribbon shows lives IN the bag (incl. selRange).
    setObjProps((prev) => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
  }, [editor, selectedObject]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Re-mirror whenever the object actually changes (undo/redo, nudges,
  // crop apply, edits made from the other surface).
  useEffect(() => {
    const canvas = editor?.canvas;
    if (!canvas) return undefined;
    const onModified = () => refresh();
    canvas.on('object:modified', onModified);
    // isEditing is mirrored — object:modified does NOT fire on enter/exit,
    // and the panel's Shape guards run on the mirror.
    canvas.on('text:editing:entered', onModified);
    canvas.on('text:editing:exited', onModified);
    // selectionStyle tracks the HIGHLIGHTED characters, which change without
    // the object ever being modified — drag a selection across a word and the
    // emphasis controls must re-read that run.
    canvas.on('selection:changed', onModified);
    canvas.on('text:selection:changed', onModified);
    return () => {
      canvas.off('object:modified', onModified);
      canvas.off('text:editing:entered', onModified);
      canvas.off('text:editing:exited', onModified);
      canvas.off('selection:changed', onModified);
      canvas.off('text:selection:changed', onModified);
    };
  }, [editor?.canvas, refresh]);

  // ---- commit machinery ----
  const commitTimerRef = useRef(null);
  // Last async mutation still in flight (texture/gradient resolve). A
  // slider's pointer-up commitNow must snapshot AFTER it lands, or the final
  // tick is absent from history/autosave.
  const pendingRef = useRef(null);
  const commitNow = useCallback(() => {
    if (commitTimerRef.current) {
      clearTimeout(commitTimerRef.current);
      commitTimerRef.current = null;
    }
    const pending = pendingRef.current;
    if (pending) {
      pending.then(
        () => { refresh(); editor?.commit?.(); },
        () => {},
      );
      return;
    }
    editor?.commit?.();
  }, [editor, refresh]);
  const commitSoon = useCallback(() => {
    if (commitTimerRef.current) clearTimeout(commitTimerRef.current);
    commitTimerRef.current = setTimeout(() => {
      commitTimerRef.current = null;
      editor?.commit?.();
    }, 500);
  }, [editor]);
  // Flush a pending debounced commit on UNMOUNT so the edit isn't lost.
  // Unmount-only by design (R1 fix): the old [editor] dependency made the
  // cleanup fire on every editor-facade rebuild — and verbs that also write
  // editor state (changeFillColor → setFillColor) rebuild the facade
  // per tick, so a 'soon' burst flushed mid-debounce and landed as TWO
  // history entries, violating the one-entry law this hook documents.
  const editorRef = useRef(editor);
  editorRef.current = editor;
  useEffect(() => () => {
    if (commitTimerRef.current) {
      clearTimeout(commitTimerRef.current);
      editorRef.current?.commit?.();
    }
  }, []);

  const apply = useCallback((fn, mode = 'now') => {
    const result = fn();
    // Promise-aware: async mutators (texture/gradient resolution) mirror and
    // commit only after they settle — committing mid-flight would snapshot
    // the pre-resolve state. Rejection re-mirrors without committing.
    if (result && typeof result.then === 'function') {
      pendingRef.current = result; // commitNow (slider pointer-up) awaits it
      result.then(
        () => {
          if (pendingRef.current === result) pendingRef.current = null;
          refresh();
          if (mode === 'now') commitNow();
          else if (mode === 'soon') commitSoon();
        },
        () => {
          if (pendingRef.current === result) pendingRef.current = null;
          refresh();
        },
      );
      return;
    }
    refresh();
    if (mode === 'now') commitNow();
    else if (mode === 'soon') commitSoon();
    // 'defer': the control commits on pointer-up via commitNow
  }, [refresh, commitNow, commitSoon]);

  // Border applies to every object in a multi-selection
  const setBorder = useCallback((color, width, mode = 'soon') => {
    apply(() => {
      const canvas = editor?.canvas;
      if (!canvas) return;
      canvas.getActiveObjects().forEach((o) => {
        o.set({ stroke: width > 0 ? color : null, strokeWidth: width, strokeUniform: true });
      });
      canvas.renderAll();
    }, mode);
  }, [apply, editor]);

  return { objProps, refresh, apply, commitNow, commitSoon, setBorder };
}

export { isTextType };
